import { useEffect, useRef, useState } from "react";
import { Carousel } from "antd";
import { Link } from "react-router-dom";
import { scroll } from "../helperFunctions/scroller";

const Stars = ({ rating }) => (
  <div className="flex justify-center space-x-1">
    {[...Array(5)].map((_, index) => (
      <span
        key={index}
        className={`text-lg ${
          index < rating ? "text-yellow-600" : "text-gray-200"
        }`}
      >
        ★
      </span>
    ))}
  </div>
);

const ReviewList = () => {
  const [reviews, setReviews] = useState([]);
  const sectionRef = useRef(null);

  // This component lives in the Footer, which renders on every route, so a
  // static import would put the Firestore SDK (~90 KB gzipped) on the critical
  // path sitewide for a widget below the fold. Instead the SDK is fetched only
  // once the section is close to being seen.
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return undefined;

    let unsubscribe;
    let cancelled = false;

    const subscribe = async () => {
      const { db, collection, onSnapshot } = await import("../firebase");
      // The observer can fire after unmount; don't open a listener we've
      // already decided to tear down.
      if (cancelled) return;

      unsubscribe = onSnapshot(collection(db, "reviews"), (snapshot) => {
        const updatedReviews = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
          .filter((review) => review.accepted)
          .sort(
            (a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0)
          );
        setReviews(updatedReviews);
      });
    };

    // No IntersectionObserver (old browser, or a test environment): just load.
    if (typeof IntersectionObserver === "undefined") {
      subscribe();
      return () => {
        cancelled = true;
        if (unsubscribe) unsubscribe();
      };
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          subscribe();
        }
      },
      // Start fetching a little before the footer scrolls into view.
      { rootMargin: "300px" }
    );
    observer.observe(node);

    return () => {
      cancelled = true;
      observer.disconnect();
      if (unsubscribe) unsubscribe();
    };
  }, []);

  return (
    <div ref={sectionRef} className="py-14 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="text-center">
          <h2 className="text-gray-900 dark:text-white font-title xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl">
            Wat onze klanten zeggen
          </h2>
          <p className="font-text text-grey dark:text-gray-300 max-w-md mx-auto mt-3">
            Ervaringen van de mensen die we voor de lens mochten hebben.
          </p>
        </div>

        {reviews.length > 0 && (
          <div className="review-carousel mt-12">
            <Carousel
              autoplay
              autoplaySpeed={10000}
              dots={true}
              slidesToShow={Math.min(3, reviews.length)}
              slidesToScroll={1}
              responsive={[
                {
                  breakpoint: 1024,
                  settings: { slidesToShow: Math.min(2, reviews.length) },
                },
                { breakpoint: 768, settings: { slidesToShow: 1 } },
              ]}
            >
              {reviews.map((review) => (
                <div key={review.id} className="h-full px-3">
                  <figure className="h-full flex flex-col bg-white p-8 rounded border border-gray-200 transition duration-300 shadow-sm hover:shadow">
                    <span
                      aria-hidden="true"
                      className="font-title text-6xl leading-none text-yellow-600/25 select-none"
                    >
                      &ldquo;
                    </span>

                    <blockquote className="review-comment font-text text-grey leading-relaxed flex-grow -mt-3">
                      {review.comment}
                    </blockquote>

                    <figcaption className="mt-6 pt-5 border-t border-gray-100">
                      <Stars rating={review.rating} />
                      <p className="font-title text-lg text-gray-900 text-center mt-2">
                        {review.username}
                      </p>
                    </figcaption>
                  </figure>
                </div>
              ))}
            </Carousel>
          </div>
        )}

        <div className="flex justify-center mt-10">
          <Link to={"/review"} onClick={() => scroll("top")}>
            <button className="font-text font-bold uppercase tracking-wider text-sm text-white bg-yellow-600 py-4 px-10 transition duration-300 hover:bg-yellow-700 focus:outline-none focus:ring-4 focus:ring-yellow-300">
              Review schrijven
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ReviewList;
